import Loader from 'components/Loader';
import { ContentLayout } from 'layouts/ContentLayout/ContentLayout';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { getCustomRoleById } from 'screens/RolesPermissions/utils';
import { format, parseISO } from 'date-fns';
import CustomRoleHeader from './CustomRoleHeader';
import Preview from './Form/Preview';
import {
  getPermissionDisplayObject,
  getTextForSystemRole,
  isSystemRole,
  transformPermissions,
} from './utils';
import Divider from 'components/Divider';

const CustomRole = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery(
    ['custom-role-details', id],
    () => getCustomRoleById(id as string),
    {
      refetchOnMount: true,
    },
  );

  const { accessexpress, nocexpress, techexpress } = getPermissionDisplayObject(
    transformPermissions(data?.permissions.map((p) => p.name) ?? []),
  );
  if (isLoading) return <Loader className="flex-1" />;
  if (data)
    return (
      <ContentLayout className="min-h-screen !justify-start">
        <CustomRoleHeader
          title={getTextForSystemRole(data.name)}
          breadcrumbs={[{ label: getTextForSystemRole(data.name) }]}
          {...(!isSystemRole(data.name) && {
            buttonAction: {
              onClick: () => navigate(`../edit/${id}`),
              children: 'Edit role',
              size: 'large',
              className: 'md:w-max w-full',
            },
          })}
        />
        <div className="flex flex-1 flex-col flex-wrap justify-between overflow-hidden pt-12 xl:flex-row">
          <div className="w-full rounded-tl rounded-tr bg-background-base-surface-3 px-8 py-6 xl:max-w-[564px] xl:rounded-bl xl:rounded-tr-none">
            <p className="mb-8 text-2xl font-bold text-content-base-default">
              Role details
            </p>
            <div className="flex flex-col gap-6">
              <div className="font-bold">
                <p>Name</p>
                <p className="text-content-base-default">
                  {getTextForSystemRole(data.name)}
                </p>
              </div>
              <div className="font-bold">
                <p>Description</p>
                <p className="text-content-base-default">{data.description}</p>
              </div>
              <div className="font-bold">
                <p>Created on</p>
                <p className="text-content-base-default">
                  {data.created_at
                    ? format(parseISO(data.created_at), 'MM/dd/yyyy')
                    : '--'}
                </p>
              </div>
              <div className="font-bold">
                <p>Last modified</p>
                <p className="text-content-base-default">
                  {data.updated_at
                    ? format(parseISO(data.updated_at), 'MM/dd/yyyy')
                    : '--'}
                </p>
              </div>
              <div className="font-bold">
                <p>Total # of users </p>
                <p className="font-fire text-content-base-default">
                  {data.user_count}
                </p>
              </div>
            </div>
          </div>
          <div className="flex-1 rounded-bl rounded-br bg-background-base-surface-2 px-8 py-6 xl:rounded-bl-none  xl:rounded-tr">
            <p className="text-2xl font-bold text-content-base-default">
              Role permissions
            </p>
            <div className="mt-8 w-full">
              <p className="mb-4 font-bold text-content-base-subdued">
                Feature permissions
              </p>
              <div className="grid grid-cols-3 gap-6">
                <div className="flex min-w-[211px] flex-col gap-2">
                  <p className="font-bold text-content-base-subdued">
                    Order services
                  </p>
                  <p className="text-sm text-content-base-default">
                    {accessexpress}
                  </p>
                </div>
                <div className="flex min-w-[211px] flex-col gap-2">
                  <p className="font-bold text-content-base-subdued">
                    Open a repair
                  </p>
                  <p className="text-sm text-content-base-default">
                    {nocexpress}
                  </p>
                </div>

                <div className="flex min-w-[211px] flex-col gap-2">
                  <p className="font-bold text-content-base-subdued">
                    Dispatch a tech
                  </p>
                  <p className="text-sm text-content-base-default">
                    {techexpress}
                  </p>
                </div>
              </div>
            </div>
            <Divider className="my-6" />
            <Preview
              name={data.name}
              color={data.color}
              description={data.description}
            />
          </div>
        </div>
      </ContentLayout>
    );
};

export default CustomRole;
